// Helpers for declaring + asserting EngineCapabilities per RFC-101.

import type { EngineId, LayerKind } from '@geosoft/viewer-sdk';
import {
  LAYER_KIND_MATRIX,
  UnsupportedFeatureError,
  type EngineCapabilities,
  type PluginKind,
  type ThreeDTilesSupport,
} from './types';

export interface DeclareCapabilitiesInput {
  engineId: EngineId;
  supportedLayerKinds: ReadonlyArray<LayerKind>;
  supportsTime?: boolean;
  supportsTerrain?: boolean;
  supports3DTiles?: ThreeDTilesSupport;
  supportsInterleaving?: ReadonlyArray<EngineId>;
  defenceVariantSafe?: boolean;
  pluginHostKinds?: ReadonlyArray<PluginKind>;
}

/**
 * Convenience builder. Defaults to the strictest plausible posture (no time,
 * no terrain, no 3D Tiles, no interleaving, defence-variant safe, no plugin
 * host kinds). Adapters override what they actually support.
 */
export function declareCapabilities(
  input: DeclareCapabilitiesInput,
): EngineCapabilities {
  return Object.freeze({
    engineId: input.engineId,
    supportedLayerKinds: Object.freeze([...input.supportedLayerKinds]),
    supportsTime: input.supportsTime ?? false,
    supportsTerrain: input.supportsTerrain ?? false,
    supports3DTiles: input.supports3DTiles ?? 'none',
    supportsInterleaving: Object.freeze([...(input.supportsInterleaving ?? [])]),
    defenceVariantSafe: input.defenceVariantSafe ?? true,
    pluginHostKinds: Object.freeze([...(input.pluginHostKinds ?? [])]),
  });
}

/**
 * Returns the missing layer kinds (if any) where the adapter's capabilities
 * fall short of the LAYER_KIND_MATRIX MUST set for its engineId. Empty array
 * = compliant.
 *
 * Used by tests and (eventually) by the ViewerHost at registration to fail fast
 * on a non-compliant adapter.
 */
export function matrixComplianceGap(caps: EngineCapabilities): LayerKind[] {
  const required = LAYER_KIND_MATRIX[caps.engineId];
  const supplied = new Set<LayerKind>(caps.supportedLayerKinds);
  const gap: LayerKind[] = [];
  for (const kind of required) {
    if (!supplied.has(kind)) gap.push(kind);
  }
  return gap;
}

export function isMatrixCompliant(caps: EngineCapabilities): boolean {
  return matrixComplianceGap(caps).length === 0;
}

/**
 * Asserts that the adapter satisfies the matrix; throws otherwise. Intended for
 * production-build host registration paths.
 */
export function assertMatrixCompliance(caps: EngineCapabilities): void {
  const gap = matrixComplianceGap(caps);
  if (gap.length > 0) {
    throw new UnsupportedFeatureError(
      caps.engineId,
      `layer-kinds:${gap.join(',')}`,
      `engine '${caps.engineId}' must support [${LAYER_KIND_MATRIX[caps.engineId].join(', ')}] per RFC-101 P3 (production matrix); missing [${gap.join(', ')}]`,
    );
  }
}

/**
 * Throws UnsupportedFeatureError if the adapter doesn't support the requested
 * feature. Convenience wrapper for adapter implementations that want to fail
 * cleanly on unsupported ops.
 *
 * Adapters call e.g. `assertCapability(caps, 'terrain', caps.supportsTerrain)`
 * inside their addLayer / setTerrain paths.
 */
export function assertCapability(
  caps: EngineCapabilities,
  feature: string,
  supported: boolean,
  hint?: string,
): void {
  if (!supported) {
    throw new UnsupportedFeatureError(caps.engineId, feature, hint);
  }
}

/**
 * Predicate the host uses to filter adapters at registration when the
 * defence-variant flag is on (RFC-101 P7).
 */
export function isAllowedInDefenceVariant(caps: EngineCapabilities): boolean {
  return caps.defenceVariantSafe === true;
}
